<template>
  <div v-if="isLoading" class="flex flex-col gap-3">
    <div class="skeleton h-6 w-full" />
    <div class="skeleton h-6 w-full" />
    <div class="skeleton h-6 w-full" />
  </div>

  <div v-else-if="request" class="flex flex-col gap-6">
    <dl class="grid max-w-md grid-cols-[8rem_1fr] gap-y-3 text-sm">
      <dt class="font-semibold text-text-secondary">{{ t('iam.access-requests.column.requester') }}</dt>
      <dd>{{ request.requesterName || '-' }}</dd>

      <dt class="font-semibold text-text-secondary">{{ t('iam.access-requests.column.email') }}</dt>
      <dd>{{ request.requesterEmail }}</dd>

      <dt class="font-semibold text-text-secondary">{{ t('iam.access-requests.column.status') }}</dt>
      <dd>
        <AccessRequestStatusBadge :status="request.status" />
      </dd>

      <dt class="font-semibold text-text-secondary">{{ t('iam.access-requests.column.access-to') }}</dt>
      <dd>
        <AccessRequestTargetCell :request="request" />
      </dd>

      <dt class="font-semibold text-text-secondary">{{ t('iam.access-requests.column.requested-at') }}</dt>
      <dd>{{ formatDateTime(request.requestedAt) }}</dd>
    </dl>

    <div v-if="request.status === AccessRequestStatus.PENDING" class="flex max-w-md flex-col gap-3">
      <InfuseNote v-if="canApprove && selectedRoleIds.length === 0" type="warn">
        {{ t('iam.access-requests.approve.role-required') }}
      </InfuseNote>
      <InfuseField
        v-if="canApprove"
        :label="t('iam.access-requests.approve.role-label')"
      >
        <RoleMultiSelect
          v-model="selectedRoleIds"
          :required="true"
          :level="level"
          :parent-id="parentId"
        />
      </InfuseField>

      <div class="flex gap-2">
        <InfuseButton
          v-if="canApprove"
          class="w-full"
          type="button"
          color="success-full"
          :disabled="isApproving || selectedRoleIds.length === 0"
          :loading="isApproving"
          @click="onAccept"
        >
          <font-awesome-icon :icon="['fas', 'check']" class="mr-2" />
          {{ t('iam.access-requests.action.approve') }}
        </InfuseButton>

        <InfuseButton
          v-if="canDeny"
          class="w-full"
          type="button"
          color="alter-full"
          :disabled="isDenying"
          @click="denyModalVisible = true"
        >
          <font-awesome-icon :icon="['fas', 'xmark']" class="mr-2" />
          {{ t('iam.access-requests.action.deny') }}
        </InfuseButton>
      </div>
    </div>
  </div>

  <InfuseConfirmModal
    v-if="denyModalVisible && request"
    :action-title="t('iam.access-requests.deny.confirm-title')"
    :action-description="
      t('iam.access-requests.deny.confirm-message', {
        requester: request.requesterName || request.requesterEmail
      })
    "
    :verification-required="false"
    :submit-text="t('iam.access-requests.action.deny')"
    :loading="isDenying"
    @confirm="onDeny"
    @close="denyModalVisible = false"
  />
</template>

<script setup lang="ts">
import {
  AccessRequest,
  AccessRequestStatus,
  accessRequestPermissions
} from '@infuse/api/iam/access-request';
import { organisationUserPermissions } from '@infuse/api/iam/organisation';
import { tenantUserPermissions } from '@infuse/api/iam/tenant';
import {
  useApproveAccessRequest,
  useDenyAccessRequest
} from '@infuse/iam/access-request/mutations';
import {
  AccessRequestStatusBadge,
  AccessRequestTargetCell
} from '@infuse/iam/access-request/ui';
import { RoleMultiSelect } from '@infuse/iam/role/ui';
import { useCurrentUserStore } from '@infuse/iam/user/store';
import { UserLevel } from '@infuse/iam/user/type';
import { InfuseButton, InfuseConfirmModal, InfuseField, InfuseNote } from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUserStore = useCurrentUserStore();

const props = defineProps<{
  request?: AccessRequest;
  isLoading: boolean;
}>();

const emit = defineEmits<{
  (e: 'refetch'): void;
}>();

const { mutateAsync: approveRequest, isPending: isApproving } = useApproveAccessRequest();
const { mutateAsync: denyRequest, isPending: isDenying } = useDenyAccessRequest();

const selectedRoleIds = ref<string[]>([]);
const denyModalVisible = ref(false);

watch(
  () => props.request?.id,
  () => {
    selectedRoleIds.value = [];
    denyModalVisible.value = false;
  }
);

const level = computed<UserLevel>(() =>
  props.request?.targetOrganisationId ? UserLevel.ORGANISATION : UserLevel.TENANT
);

const parentId = computed<string | undefined>(
  () => props.request?.targetOrganisationId ?? props.request?.targetTenantId
);

const canApprove = computed(() => {
  if (props.request?.targetOrganisationId) {
    return currentUserStore.hasPermissions([
      organisationUserPermissions.GET,
      organisationUserPermissions.ADD_USERS,
      organisationUserPermissions.ADD_ROLES
    ]);
  }
  if (props.request?.targetTenantId) {
    return currentUserStore.hasPermissions([
      tenantUserPermissions.GET,
      tenantUserPermissions.ADD_USERS,
      tenantUserPermissions.ADD_ROLES
    ]);
  }
  return false;
});

const canDeny = computed(() => currentUserStore.hasPermission(accessRequestPermissions.DENY));

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString();
}

function parentLocation() {
  const name = (route.meta?.slideInParent as string | undefined) ?? 'access-requests';
  return { name, params: { ...route.params } };
}

async function onAccept(): Promise<void> {
  if (!props.request || selectedRoleIds.value.length === 0) {
    return;
  }
  await approveRequest({ request: props.request, roleIds: selectedRoleIds.value });
  emit('refetch');
  await router.push(parentLocation());
}

async function onDeny(): Promise<void> {
  if (!props.request) {
    return;
  }
  await denyRequest(props.request.id);
  denyModalVisible.value = false;
  emit('refetch');
  await router.push(parentLocation());
}
</script>
